import React, { useContext, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { useCustomer, updateProfile } from '@techstyle/react-accounts';
import { FormattedMessage } from '@techstyle/react-intl';
import { useDomain } from '@techstyle/redux-core';

import { SignUpMethod } from '../../constants/auth';
import { mobile } from '../../styles';
import { Container as SignIn } from '../SignIn';
import { Component as SignInContent } from '../SignInContent';
import ToastContext from '../ToastContext/ToastContext';

const SIGNUP = 'SIGN_UP';
const SIGNIN = 'SIGN_IN';
const FORGOT_PASSWORD = 'FORGOT_PASSWORD';

const StyledScreenWrapper = styled.div`
  justify-content: center;
  padding-top: ${({ theme }) => theme.spacing.small}px;

  ${SignInContent.Form} {
    background-color: transparent;
    padding: 0px ${({ theme }) => theme.spacing.small}px;
    margin-top: 20px;

    @media (min-width: ${({ theme }) => theme.breakpoints.large}px) {
      padding: 0px 175px;
    }
  }

  ${SignInContent.FormField} fieldset {
    margin-top: ${({ theme }) => theme.spacing.small}px;
  }

  ${SignInContent.StyledPasswordField} {
    margin-top: ${({ theme }) => theme.spacing.small}px;

    & fieldset {
      margin-top: 0;
    }
  }

  ${SignInContent.Cell} {
    background-color: transparent;
    padding-bottom: 0px;
    border-top: solid 1px ${({ theme }) => theme.colors.lavender400};
  }

  ${SignInContent.CellFooter} {
    display: flex;
    flex-direction: row;
    justify-content: left;
    margin-top: 30px;
  }

  ${SignInContent.FormTitle} {
    display: none;
  }

  ${SignInContent.FormSubtitle} {
    display: none;
  }

  ${SignInContent.BottomForgotPassword}, ${SignInContent.BackToSignIn} {
    display: flex;
    flex-direction: row;
    justify-content: start;
    margin-top: ${({ theme }) => theme.spacing.medium}px;
  }

  ${SignInContent.InstructionsTop} {
    display: none;
  }

  ${SignInContent.PrivacyTermsConditionsBox} {
    margin-top: 20px;
    font-size: 12px;
    line-height: 14px;
    font-weight: 600;
    justify-content: center;
    ${mobile`
      justify-content: left;
    `};
  }

  ${SignInContent.PrivacyNoticeMessage} {
    position: relative;
  }

  ${SignInContent.SubscribeToEmailWrapper} {
    position: relative;
    padding-top: 28px;
  }

  ${SignInContent.ToastContainer} {
    z-index: 10;
  }

  ${SignInContent.RegistrationModalHeadline} {
    display: none;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.large}px;
  ${mobile`
    align-items: start;
    padding: 0px ${({ theme }) => theme.spacing.small}px;
  `};
`;

const CreateProfileText = styled.div`
  color: ${({ theme }) => theme.colors.default};
  font-weight: 300;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  letter-spacing: 0.3px;
`;

const ConfirmText = styled.div`
  color: ${({ theme }) => theme.colors.default};
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  letter-spacing: 0.3px;
`;

const SignUpWrapper = styled.div`
  display: flex;
  flex: 0 0 auto;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.medium}px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${mobile`
    align-items: start;
    margin-bottom: ${({ theme }) => theme.spacing.moderate}px;
  `};
`;

export default function SizeFilterModalSignUpScreen({
  selectedSizes,
  onSuccess = () => {},
}) {
  const { tld } = useDomain();
  const dispatch = useDispatch();
  const profile = useCustomer();
  const { showMessage: showToastMessage } = useContext(ToastContext.Context);
  const [currentScreenType, setCurrentScreenType] = useState(SIGNUP);
  const newProfile = {
    'bottom-size': selectedSizes.undieSizes[0] || '',
    'bra-size': selectedSizes.braSizes[0] || '',
    'bralette-size': selectedSizes.braletteSizes[0] || '',
    'lingerie-sleep-size': selectedSizes.lingerieSizes[0] || '',
  };

  const onSignInClick = () => {
    setCurrentScreenType(SIGNIN);
  };

  const onSignUpClick = () => {
    setCurrentScreenType(SIGNUP);
  };

  const onForgotPasswordLinkClick = () => {
    setCurrentScreenType(FORGOT_PASSWORD);
  };

  const showErrorMessage = () => {
    showToastMessage(
      <FormattedMessage
        id="global_quiz.signup_form_error_message"
        defaultMessage="Please fill out the forms below"
      />
    );
  };

  const onSignInSuccess = async () => {
    await dispatch(updateProfile({ ...profile, profile: { ...newProfile } }));
    onSuccess();
  };

  const renderSignUpScreen = () => {
    let confirmText = null;
    switch (currentScreenType) {
      case SIGNIN:
        confirmText = (
          <FormattedMessage
            defaultMessage="Already have an account?"
            id="global_checkout.registration_modal_already_have_account"
          />
        );
        break;
      case SIGNUP:
        confirmText = (
          <FormattedMessage
            defaultMessage="Thanks for confirming!"
            id="site_product_grid.signup_confirm_text"
          />
        );

        break;
      case FORGOT_PASSWORD:
        confirmText = (
          <FormattedMessage
            defaultMessage="Forgot password?"
            id="global_checkout.forgot_password_link"
          />
        );

        break;
      default:
        confirmText = (
          <FormattedMessage
            defaultMessage="Thanks for confirming!"
            id="site_product_grid.signup_confirm_text"
          />
        );
    }

    return (
      <>
        <TextWrapper>
          <ConfirmText>{confirmText}</ConfirmText>
          {currentScreenType !== SIGNIN ? (
            <CreateProfileText>
              {currentScreenType === SIGNUP ? (
                <FormattedMessage
                  defaultMessage="Create your profile now."
                  id="site_product_grid.signup_create_text"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Enter email below to reset your password"
                  id="site_product_grid.forgot_password"
                />
              )}
            </CreateProfileText>
          ) : null}
        </TextWrapper>
        <SignUpWrapper>
          <SignIn
            extraSignUpData={{
              method: SignUpMethod.SIZE_FILTERS,
              profile: { ...newProfile },
            }}
            initialScreen="SIGN_UP"
            onForgotPasswordLinkClick={onForgotPasswordLinkClick}
            onFormErrors={showErrorMessage}
            onSignIn={onSignInSuccess}
            onSignInClick={onSignInClick}
            onSignUp={onSuccess}
            onSignUpClick={onSignUpClick}
            registrationModal
            setToggledState={setCurrentScreenType}
            shouldEnableSubmitBtn
            shouldShowFooterForgotPasswordLink
            shouldShowPrivacyTermsBeforeCTA
            showSwitcher={false}
            signUpLabel={
              <FormattedMessage
                id="site_product_grid.complete_signup_label"
                defaultMessage="create my profile"
              />
            }
            sizeFilterSignUp
            showReferrerField
          />
        </SignUpWrapper>
      </>
    );
  };

  return (
    <StyledScreenWrapper tld={tld}>{renderSignUpScreen()}</StyledScreenWrapper>
  );
}

SizeFilterModalSignUpScreen.propTypes = {
  selectedSizes: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
